import React, { useState, useEffect } from 'react';
import { X, Search, Settings, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchInventarioInsumos } from '@/pages/lavanderia/insumos/services/inventario-insumos.services';

const SeleccionarInsumoAjusteModal = ({ isOpen, onClose, onSelectInsumo }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [insumos, setInsumos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadInsumos();
    }
  }, [isOpen]);

  const loadInsumos = async () => {
    setIsLoading(true);
    try {
      const response = await fetchInventarioInsumos({
        page: 1,
        limit: 1000,
        offset: 0,
        query: {}
      });
      setInsumos(response.data || []);
    } catch (error) {
      console.error('Error al cargar insumos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los insumos',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInsumos = insumos.filter(insumo => {
    const nombre = insumo.consumible?.nombre || insumo.consumible || '';
    return nombre.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelectInsumo = (insumo) => {
    onSelectInsumo(insumo);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Settings className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Seleccionar Insumo para Ajustar
              </h2>
              <p className="text-sm text-gray-600">
                Elige el insumo que deseas ajustar o dar de baja
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar insumo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando insumos...</p>
              </div>
            </div>
          ) : filteredInsumos.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'No se encontraron insumos' : 'No hay insumos en el inventario'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredInsumos.map((insumo) => (
                <div
                  key={insumo._id}
                  onClick={() => handleSelectInsumo(insumo)}
                  className="border rounded-lg p-4 hover:bg-orange-50 hover:border-orange-300 cursor-pointer transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {insumo.consumible?.nombre || insumo.consumible}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span>Disponible: <strong>{insumo.cantidad_disponible}</strong></span>
                        <span>Mínima: {insumo.cantidad_minima}</span>
                        {insumo.ubicacion && <span>Ubicación: {insumo.ubicacion}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${
                        insumo.estado === 'disponible' ? 'bg-green-100 text-green-800' :
                        insumo.estado === 'bajo_stock' ? 'bg-yellow-100 text-yellow-800' :
                        insumo.estado === 'agotado' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {insumo.estado}
                      </Badge>
                      <Button
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        Seleccionar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionarInsumoAjusteModal;
